import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { BlockButton } from '@/components/BlockButton';
import { zodResolver } from '@hookform/resolvers/zod';
import type { CandidatesMypageSettingSchema } from '../schemas/candidatesMypageSettingSchema';
import { candidatesMypageSettingSchema } from '../schemas/candidatesMypageSettingSchema';
import { TextField } from '@/components/TextField';
import { STATUS_ITEMS } from '../constants';
import { Radio } from '@/components/Radio';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ChangeEvent } from 'react';
import { MdOpenInNew, MdLink } from 'react-icons/md';
import { showSuccessToast, showErrorToast } from '../../job_change_processes/lib/toast';
import useFetchCandidateEndpoint from '../api/useFetchCandidateEndpoint';
import useUpdateCandidateEndpointsProfile from '../api/useUpdateCandidateEndpointsProfile';
import { Button } from '@/components/Button';
import { Switch } from '@/components/ui/switch';
import Checkbox from '../../../../components/Checkbox';

type Props = {
  candidateId: number;
};

export default function CandidatesMypageSettingCard({ candidateId }: Props) {
  const { data: candidateEndpoint, isLoading: isCandidateLoading } = useFetchCandidateEndpoint({
    id: candidateId,
  });
  const { mutateAsync: updateProfile } = useUpdateCandidateEndpointsProfile();
  const queryClient = useQueryClient();
  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
    setValue,
    watch,
    control,
    reset,
  } = useForm<CandidatesMypageSettingSchema>({
    resolver: zodResolver(candidatesMypageSettingSchema),
    mode: 'all',
    defaultValues: {
      title: '',
      status: '',
      is_show_job_post: false,
      is_show_process: false,
      is_show_uploader: false,
      is_login_required: false,
      login_id: '',
      password: '',
    },
  });

  useEffect(() => {
    if (!isCandidateLoading && candidateEndpoint) {
      reset({
        title: candidateEndpoint.candidate_endpoint.title || '',
        status: candidateEndpoint.candidate_endpoint.status || '',
        is_show_job_post: candidateEndpoint.candidate_endpoint.is_show_job_post ?? false,
        is_show_process: candidateEndpoint.candidate_endpoint.is_show_process ?? false,
        is_show_uploader: candidateEndpoint.candidate_endpoint.is_show_uploader ?? false,
        is_login_required: candidateEndpoint.candidate_endpoint.is_login_required ?? false,
        login_id:
          candidateEndpoint.candidate_endpoint.login_id ||
          candidateEndpoint.candidate_endpoint.default_login_id ||
          '',
        password: '',
      });
    }
  }, [isCandidateLoading, reset, candidateEndpoint]);
  const status = watch('status');
  const isLoginRequired = watch('is_login_required');
  const isPortalLoginAvailable = candidateEndpoint?.candidate_endpoint.is_portal_login_available;
  const hasPassword = candidateEndpoint?.candidate_endpoint.has_password ?? false;

  const onChangeStep = (e: ChangeEvent<HTMLInputElement>) => {
    setValue('status', e.currentTarget.value);
  };

  const handleCopyUrl = async () => {
    const path = candidateEndpoint?.candidate_endpoint.url;
    // URL未取得（ロード中・取得失敗）の場合は origin だけコピーしないようガードする
    if (!path) {
      showErrorToast('URLの取得に失敗しました');
      return;
    }
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${path}`);
      showSuccessToast('URLをコピーしました');
    } catch (error) {
      showErrorToast('URLのコピーに失敗しました');
    }
  };

  const onSubmit: SubmitHandler<CandidatesMypageSettingSchema> = async (data) => {
    try {
      const result = await updateProfile({
        title: data.title,
        status: data.status,
        is_show_job_post: data.is_show_job_post,
        is_show_process: data.is_show_process,
        is_show_uploader: data.is_show_uploader,
        ...(isPortalLoginAvailable
          ? {
              is_login_required: data.is_login_required,
              login_id: data.login_id,
              password: data.password,
            }
          : {}),
        candidateProfileId: candidateId,
      });
      if (result?.success === false) {
        showErrorToast(result.error || '更新に失敗しました');
        return;
      }
      setValue('password', '');
      await queryClient.invalidateQueries({ queryKey: ['candidateEndpoint', candidateId] });
      showSuccessToast('更新しました');
    } catch (error) {
      showErrorToast('更新に失敗しました');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <form
        action=""
        className="rounded bg-white shadow-[0px_0px_6px_0px_#00000033]"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex items-center justify-between gap-md px-lg py-md">
          <p className="text-heading-xs">マイページ設定</p>
          <div className="flex items-center gap-xs">
            <Button asChild color="tertiary" size="sm">
              <a
                href={candidateEndpoint?.candidate_endpoint.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                求職者マイページ
                <MdOpenInNew size={16} />
              </a>
            </Button>
            <Button type="button" color="secondary" size="sm" onClick={handleCopyUrl}>
              <MdLink size={16} />
              URL共有
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-lg px-lg pt-sm pb-lg">
          <div className="flex w-full flex-col gap-xs">
            <p className="text-heading-xxs">マイページに表示する候補者名</p>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="マイページに表示する候補者名を入力してください"
                />
              )}
            />
          </div>
          <div className="flex w-full flex-col gap-xs">
            <p className="text-heading-xxs">マイページの公開設定</p>
            <div className="flex gap-5">
              {STATUS_ITEMS.map((m, key) => {
                return (
                  <Controller
                    key={key}
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <Radio
                        {...field}
                        value={m.value}
                        checked={status === m.value}
                        onChange={onChangeStep}
                        labelClassName="font-normal"
                      >
                        {m.label}
                      </Radio>
                    )}
                  />
                );
              })}
            </div>
          </div>
          <div className="flex w-full flex-col gap-xs">
            <p className="text-heading-xxs">マイページに表示するタブ</p>
            <div className="flex gap-3.5 text-body-sm">
              <Controller
                name="is_show_job_post"
                control={control}
                render={({ field }) => (
                  <Checkbox checked={field.value} onChange={field.onChange}>
                    ご提案求人ページ
                  </Checkbox>
                )}
              />
              <Controller
                name="is_show_process"
                control={control}
                render={({ field }) => (
                  <Checkbox checked={field.value} onChange={field.onChange}>
                    選考中の求人ページ
                  </Checkbox>
                )}
              />
              <Controller
                name="is_show_uploader"
                control={control}
                render={({ field }) => (
                  <Checkbox checked={field.value} onChange={field.onChange}>
                    ファイルアップロード
                  </Checkbox>
                )}
              />
            </div>
          </div>
          {isPortalLoginAvailable && (
            <div className="flex w-full flex-col gap-xs">
              <p className="text-heading-xxs">ログイン設定</p>
              <Controller
                name="is_login_required"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center gap-2 text-body-sm">
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                    マイページ閲覧にログインを必須にする
                  </label>
                )}
              />
              {isLoginRequired && (
                <div className="flex flex-col gap-sm">
                  <p className="text-body-sm text-danger">
                    保存すると、現在のURLを開いた候補者もログイン画面が表示されます。ログインIDとパスワードを候補者にお伝えください。
                  </p>
                  <div className="flex flex-col gap-xs">
                    <label htmlFor="mypage-login-id" className="text-body-sm">
                      ログインID
                    </label>
                    <Controller
                      name="login_id"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id="mypage-login-id"
                          autoComplete="off"
                          placeholder="ログインIDを入力してください"
                        />
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-xs">
                    <label htmlFor="mypage-login-password" className="text-body-sm">
                      パスワード（8文字以上）
                    </label>
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id="mypage-login-password"
                          type="password"
                          autoComplete="new-password"
                          placeholder={
                            hasPassword
                              ? '設定済み（変更する場合のみ入力）'
                              : 'パスワードを入力してください'
                          }
                        />
                      )}
                    />
                  </div>
                  <p className="text-body-sm text-gray-600">
                    ログインIDとパスワードが未設定の場合は保存できません。
                  </p>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end">
            <div className="w-[80px]">
              <BlockButton text="更新する" type="submit" disabled={!isValid || isSubmitting} />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
